import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getNCR,
  deleteNCR,
  markInProgress,
  markCorrected,
  closeNCR
} from "../../../services/api/qa/ncrService";
import { fetchProjectsApi } from "../../../services/api/boq/projectService";
import toast from "react-hot-toast";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";

function NCRList() {
  const [ncrs, setNcrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);

      const ncrRes = await getNCR();

      const ncrData =
        ncrRes.data?.data?.data ||
        ncrRes.data?.data ||
        [];

      setNcrs(ncrData);

      const projectRes = await fetchProjectsApi();

      const projectData =
        projectRes.data?.data?.data ||
        projectRes.data?.data ||
        [];

      setProjects(projectData);

    } catch (err) {
      console.error("Error loading NCRs", err);
      toast.error("Failed to load NCRs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredNCRs = Array.isArray(ncrs) ? ncrs.filter((ncr) => {
    return (
      (ncr.title || "").toLowerCase().includes(search.toLowerCase()) &&
      (statusFilter ? ncr.status === statusFilter : true) &&
      (severityFilter ? ncr.severity === severityFilter : true) &&
      (projectFilter ? ncr.project_id == projectFilter : true)
    );
  }) : [];

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-yellow-200 text-yellow-800";

      case "in_progress":
        return "bg-orange-200 text-orange-800";

      case "corrected":
        return "bg-purple-200 text-purple-800";

      case "closed":
        return "bg-green-200 text-green-800";

      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteNCR(deleteId);
      toast.success("NCR deleted successfully");
      loadData();
    } catch {
      toast.error("Failed to delete NCR");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleStatusChange = async (id, action) => {
    try {
      if (action === "in_progress") await markInProgress(id);
      if (action === "corrected") await markCorrected(id);
      if (action === "closed") await closeNCR(id);
      toast.success("Status updated successfully");
      loadData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  // Counts
  const totalCount = ncrs.length;
  const openCount = ncrs.filter(n => n.status !== "closed").length;
  const inProgressCount = ncrs.filter(n => n.status === "in_progress").length;
  const closedCount = ncrs.filter(n => n.status === "closed").length;
  const highSeverityCount = ncrs.filter(n => n.severity === "high").length;
  const overdueCount = ncrs.filter(n => new Date(n.due_date) < new Date() && n.status !== "closed").length;

  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <h2 className="text-2xl font-bold">NCR List</h2>
        <Link to="/ncr/create" className="bg-blue-600 text-white px-4 py-2 rounded">
          + Create NCR
        </Link>
      </div>

      {/* Count Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {[
          { label: "Total", value: totalCount, bg: "bg-white" },
          { label: "Open", value: openCount, bg: "bg-yellow-100" },
          { label: "In Progress", value: inProgressCount, bg: "bg-orange-100" },
          { label: "Closed", value: closedCount, bg: "bg-green-100" },
          { label: "High Severity", value: highSeverityCount, bg: "bg-red-100" },
          { label: "Overdue", value: overdueCount, bg: "bg-pink-100" },
        ].map((card) => (
          <div key={card.label} className={`${card.bg} rounded p-4 text-center`}>
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="text-xl font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <input
          type="text"
          placeholder="Search by title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border p-2 rounded"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border p-2 rounded">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="corrected">Corrected</option>
          <option value="closed">Closed</option>
        </select>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} className="border p-2 rounded">
          <option value="">All Severity</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="border p-2 rounded">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
        </select>
      </div>

      {loading && <p>Loading NCRs...</p>}

      {/* Desktop Table */}
      <div className="hidden md:block bg-white shadow rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">#</th>
              <th className="p-3 text-left">Title</th>
              <th className="p-3 text-left">Project</th>
              <th className="p-3 text-left">BOQ Item</th>
              <th className="p-3 text-left">Severity</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Due Date</th>
              <th className="p-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredNCRs.map((ncr, index) => (
              <tr key={ncr.id} className="border-t hover:bg-gray-50">
                <td className="p-3">{index + 1}</td>
                <td className="p-3">{ncr.title}</td>
                <td>{ncr.project?.project_name || "-"}</td>
                <td>{ncr.boq_item?.item_name || "-"}</td>
                <td className="p-3 capitalize">{ncr.severity}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 text-xs rounded ${getStatusColor(ncr.status)}`}>{ncr.status}</span>
                </td>
                <td className="p-3">{ncr.due_date}</td>
                <td className="p-3 flex gap-2">
                  <Link
                    to={`/ncr/${ncr.id}`}
                    className="text-green-600 p-2 rounded hover:bg-green-100"
                    title="View"
                  >
                    <FaEye />
                  </Link>

                  <Link
                    to={`/ncr/edit/${ncr.id}`}
                    className="text-yellow-600 p-2 rounded hover:bg-yellow-100"
                    title="Edit"
                  >
                    <FaEdit />
                  </Link>

                  <button
                    onClick={() => {
                      setDeleteId(ncr.id);
                      setShowDeleteModal(true);
                    }}
                    className="text-red-600 px-2 py-1 rounded hover:bg-red-100"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {filteredNCRs.map((ncr) => (
          <div key={ncr.id} className="bg-white shadow rounded p-4">
            <div className="flex justify-between">
              <h3 className="font-semibold">{ncr.title}</h3>
              <span className={`px-2 py-1 text-xs rounded ${getStatusColor(ncr.status)}`}>{ncr.status}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Project:{ncr.project?.project_name || "-"}</p>
            <p className="text-sm text-gray-600">
              BOQ: {ncr.boq_item?.item_name || "-"}
            </p>
            <p className="text-sm text-gray-600">Severity: {ncr.severity}</p>
            <p className="text-sm text-gray-600">Due: {ncr.due_date}</p>
            <Link to={`/ncr/${ncr.id}`} className="text-blue-600 text-sm underline mt-2 block">View Details</Link>
          </div>
        ))}
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">

          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <h3 className="text-lg font-semibold mb-4">
              Delete NCR
            </h3>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this NCR?
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>

        </div>
      )}
    </div >
  );
}

export default NCRList;